import { isPossiblePhoneNumber, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';

/**
 * Validates a phone number using libphonenumber-js library
 * This library supports all countries and regions that react-international-phone supports
 * @param phone - The phone number to validate (in international format with country code)
 * @param t - The translation function from i18n
 * @returns object with isValid boolean and errorMessage if invalid
 */
export const validatePhoneNumberByCountryCode = (
	phone: string,
	t?: (key: string) => string
): { isValid: boolean; errorMessage?: string } => {
	try {
		if (!phone || phone.trim() === '') {
			return {
				isValid: false,
				errorMessage: t ? t('phoneRequired') : 'Phone number is required',
			};
		}

		// Remove spaces and special characters for validation
		const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

		// Basic international format check
		if (!cleanPhone.startsWith('+')) {
			return {
				isValid: false,
				errorMessage: t ? t('phoneNumberMatchError') : 'Phone number must include country code',
			};
		}

		// Use libphonenumber-js for comprehensive validation
		// First check if it's a possible phone number (basic length and format check)
		if (!isPossiblePhoneNumber(cleanPhone)) {
			return {
				isValid: false,
				errorMessage: t ? t('phoneNumberMatchError') : 'Phone number format is not valid',
			};
		}

		// Then check if it's a valid phone number (detailed validation)
		if (!isValidPhoneNumber(cleanPhone)) {
			return {
				isValid: false,
				errorMessage: t ? t('phoneNumberMatchError') : 'Phone number is not valid',
			};
		}

		return { isValid: true };
	} catch (error) {
		return {
			isValid: false,
			errorMessage: t ? t('phoneNumberMatchError') : 'Invalid phone number format',
		};
	}
};

/**
 * Gets the country info from a phone number using libphonenumber-js
 * @param phone - The phone number in international format
 * @returns object with country info or null if not found
 */
export const getCountryFromPhoneNumber = (phone: string) => {
	try {
		const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

		if (!cleanPhone.startsWith('+')) {
			return null;
		}

		const phoneNumber = parsePhoneNumber(cleanPhone);

		if (phoneNumber && phoneNumber.country) {
			return {
				iso2: phoneNumber.country,
				name: phoneNumber.country, // You might want to map this to full country names if needed
				dialCode: phoneNumber.countryCallingCode,
			};
		}

		return null;
	} catch (error) {
		return null;
	}
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use validatePhoneNumberByCountryCode instead
 */
export const validatePhoneNumberForCountry = (
	phone: string,
	selectedCountry?: { alpha_two?: string; name?: string },
	t?: (key: string) => string
): { isValid: boolean; errorMessage?: string } => {
	// Just use the new validation that extracts country from phone number
	return validatePhoneNumberByCountryCode(phone, t);
};

/**
 * Formats a phone number for display using libphonenumber-js
 * @param phone - The phone number to format
 * @returns string - The formatted phone number
 */
export const formatPhoneNumber = (phone: string): string => {
	try {
		const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

		if (!cleanPhone.startsWith('+')) {
			return phone;
		}

		const phoneNumber = parsePhoneNumber(cleanPhone);

		if (phoneNumber) {
			// Return international format
			return phoneNumber.formatInternational();
		}

		return phone;
	} catch (error) {
		return phone;
	}
};

/**
 * Additional validation function to check if phone number is possible (basic check)
 * @param phone - The phone number to validate
 * @returns boolean - true if phone number is possible
 */
export const isPhoneNumberPossible = (phone: string): boolean => {
	try {
		const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

		if (!cleanPhone.startsWith('+')) {
			return false;
		}

		return isPossiblePhoneNumber(cleanPhone);
	} catch (error) {
		return false;
	}
};

/**
 * Additional validation function to check if phone number is valid (detailed check)
 * @param phone - The phone number to validate
 * @returns boolean - true if phone number is valid
 */
export const isPhoneNumberValid = (phone: string): boolean => {
	try {
		const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');

		if (!cleanPhone.startsWith('+')) {
			return false;
		}

		return isValidPhoneNumber(cleanPhone);
	} catch (error) {
		return false;
	}
};
